import { TimePicker } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import * as React from 'react'

import BaseField, { type Props as FieldProps } from './BaseField'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

export type Props = {
  ...FieldProps,
  id?: string,
  name?: string,
  onChange: (value: null | [Date, string]) => void,
  format?: 'hh:mm A' | 'HH:mm' | 'HH:mm:ss' | 'HH:mm:ss A',
  size?: 'small' | 'middle' | 'large',
  defaultValue?: null | string | Date,
  value?: null | string | Date,
  disabled?: boolean,
  disabledGrayed?: boolean,
  utcValue?: boolean,
  valueParserFormat?: 'HH:mm:ss',
  showPastOnly?: boolean,
  showFutureOnly?: boolean,
  minTime?: null | string | Date,
  maxTime?: null | string | Date,
}

function TimeField({
  id,
  name,
  onChange,
  format = 'hh:mm A',
  size = 'large',
  defaultValue,
  value,
  disabled,
  utcValue,
  valueParserFormat = 'HH:mm:ss',
  showPastOnly,
  showFutureOnly,
  minTime,
  maxTime,
  disabledGrayed = false,
  ...rest
}: Props): React.Node {
  const valueIsString = React.useMemo(() => {
    if (!value) {
      return false
    }
    return typeof value === 'string'
  }, [value])

  const _minTime = React.useMemo(() => {
    if (minTime) {
      return dayjs(minTime, valueIsString ? valueParserFormat : undefined)
    }
    if (showFutureOnly) {
      return dayjs()
    }
    return undefined
  }, [minTime, showFutureOnly, valueIsString, valueParserFormat])

  const _maxTime = React.useMemo(() => {
    if (maxTime) {
      return dayjs(maxTime, valueIsString ? valueParserFormat : undefined)
    }
    if (showPastOnly) {
      return dayjs()
    }
    return undefined
  }, [maxTime, showPastOnly, valueIsString, valueParserFormat])

  const disabledTime = React.useCallback(
    (now, _) => {
      if (showPastOnly || showFutureOnly) {
        const minTime = _minTime ? _minTime.toDate() : undefined
        const maxTime = _maxTime ? _maxTime.toDate() : undefined
        const disabledHours = []
        const disabledMinutes = []

        if (minTime) {
          for (let i = 0; i < minTime.getHours(); i++) {
            disabledHours.push(i)
          }
          for (let i = 0; i < minTime.getMinutes(); i++) {
            disabledMinutes.push(i)
          }
        }

        if (maxTime) {
          for (let i = 23; i > maxTime.getHours(); i--) {
            disabledHours.push(i)
          }
          for (let i = 59; i > maxTime.getMinutes(); i--) {
            disabledMinutes.push(i)
          }
        }

        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return undefined
    },
    [_maxTime, _minTime, showFutureOnly, showPastOnly]
  )

  const styles = React.useMemo(() => {
    const _styles: {
      paddingTop: string,
      paddingBottom: string,
    } = { paddingTop: '0.438rem', paddingBottom: '0.438rem' }
    return _styles
  }, [])

  const className = React.useMemo(() => {
    let _className =
      'form-input focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] border w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 px-2'
    if (disabledGrayed) {
      _className += ' date-disabled-no-grayed-out'
    }
    return _className
  }, [disabledGrayed])

  return (
    <BaseField {...rest}>
      <TimePicker
        use12Hours
        className={className}
        defaultValue={
          defaultValue
            ? dayjs(defaultValue, valueIsString ? valueParserFormat : undefined)
            : undefined
        }
        disabled={disabled}
        disabledTime={disabledTime}
        format={
          format
            ? {
                format,
                type: 'mask',
              }
            : undefined
        }
        id={id || name}
        name={name ?? 'time_field'}
        needConfirm={false}
        onChange={(time, timeString) => {
          const _time = utcValue ? time?.utc() : time
          const _timeString = _time?.format(valueParserFormat)
          onChange(time ? [_time.toDate(), _timeString] : null)
        }}
        placeholder={format.toLowerCase()}
        style={styles}
        value={
          value
            ? dayjs(value, valueIsString ? valueParserFormat : undefined)
            : undefined
        }
        {...rest}
      />
    </BaseField>
  )
}

export default TimeField
